import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-customer-service-2-line',
      title: 'Customer First',
      description: 'Your satisfaction and energy independence are our top priorities. We listen, advise, and deliver solutions tailored to your needs.',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Quality & Reliability',
      description: 'We use only premium solar equipment and employ certified professionals to ensure your system performs flawlessly for decades.',
    },
    {
      icon: 'ri-earth-line',
      title: 'Environmental Commitment',
      description: 'Every solar installation we complete contributes to a cleaner planet and a more sustainable future for our community.',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Innovation & Expertise',
      description: 'We stay at the forefront of solar technology, bringing you the most efficient and cost-effective energy solutions available.',
    },
  ];

  return (
    <div className="font-poppins">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f5a7c4f32101188639740ca4397da5ff.jpg"
            alt="BRICK CITY SOLAR team"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-solar-blue/80 via-solar-blue/60 to-solar-blue/80"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Local Solar Experts<br />
            <span className="text-energy-yellow">Serving Ocala, Florida</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Empowering homes and businesses with clean, affordable solar energy solutions since day one.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to="/contact"
              className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl cursor-pointer inline-block whitespace-nowrap"
            >
              Schedule Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20residential%20neighborhood%20in%20Ocala%20Florida%20with%20multiple%20homes%20featuring%20solar%20panel%20installations%2C%20aerial%20view%2C%20suburban%20community%2C%20clean%20energy%20adoption%2C%20sustainable%20living%2C%20green%20technology%2C%20sunny%20day%2C%20modern%20homes%20with%20solar%20roofs%2C%20environmental%20responsibility&width=800&height=1000&seq=about-mission&orientation=portrait"
                  alt="Solar community in Ocala"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
                Our <strong>Mission</strong>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At BRICK CITY SOLAR, our mission is to make clean, renewable energy accessible and affordable for every home and business in Ocala and throughout Florida. We believe that solar energy is not just an investment in your property—it's an investment in our planet's future.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We're committed to delivering exceptional solar solutions that reduce energy costs, increase property values, and minimize environmental impact. Every installation we complete brings us one step closer to a sustainable energy future.
              </p>
              <div className="bg-soft-gray p-6 rounded-xl">
                <h3 className="text-2xl font-bold text-solar-blue mb-3">Our Vision</h3>
                <p className="text-sm text-gray-600">
                  To be Florida's most trusted solar energy provider, recognized for our commitment to quality, customer satisfaction, and environmental stewardship. We envision a future where clean solar energy powers every home and business in our community.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Local Matters */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Why Choose a <strong>Local Solar Provider</strong>?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Working with a local Ocala solar company means personalized service, faster response times, and support from neighbors who care about your community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-map-pin-user-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Local Expertise</h3>
              <p className="text-sm text-gray-600">
                We understand Florida's unique climate, weather patterns, and energy regulations. Our solutions are optimized for maximum performance in Ocala's environment.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-time-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Rapid Response</h3>
              <p className="text-sm text-gray-600">
                Being local means we're always nearby. Whether you need maintenance, support, or have questions, we're here quickly when you need us.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-community-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Community Investment</h3>
              <p className="text-sm text-gray-600">
                We're not just a business—we're your neighbors. We invest in our community and take pride in helping Ocala become more sustainable.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-hand-heart-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Personalized Service</h3>
              <p className="text-sm text-gray-600">
                You're not just a number to us. We provide personalized consultations and custom solutions designed specifically for your property and needs.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-shield-check-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Accountability</h3>
              <p className="text-sm text-gray-600">
                Our reputation in the local community matters. We stand behind our work with comprehensive warranties and ongoing support.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-solar-blue"></i>
              </div>
              <h3 className="text-xl font-bold text-solar-blue mb-3">Local Economy</h3>
              <p className="text-sm text-gray-600">
                Choosing local keeps money in our community, supports local jobs, and strengthens Ocala's economy while you save on energy costs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Our Core <strong>Values</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at BRICK CITY SOLAR.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-eco-green/10 rounded-lg mb-6">
                  <i className={`${value.icon} text-3xl text-eco-green`}></i>
                </div>
                <h3 className="text-2xl font-bold text-solar-blue mb-3">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-term Savings Philosophy */}
      <section className="py-20 bg-solar-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Investing in Your <strong>Financial Future</strong>
              </h2>
              <p className="text-lg text-white/90 mb-6">
                Solar energy isn't just about going green—it's about smart financial planning. When you invest in solar with BRICK CITY SOLAR, you're making a decision that pays dividends for decades.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Immediate Savings</h4>
                    <p className="text-sm text-white/80">Start reducing your energy bills from day one of activation.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">25+ Year Performance</h4>
                    <p className="text-sm text-white/80">Premium solar panels with warranties that guarantee decades of reliable energy production.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Protection from Rate Increases</h4>
                    <p className="text-sm text-white/80">Lock in your energy costs and avoid future utility rate hikes.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-energy-yellow mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Increased Property Value</h4>
                    <p className="text-sm text-white/80">Homes with solar sell faster and for more money than comparable homes without solar.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm p-10 rounded-2xl"
            >
              <h3 className="text-3xl font-bold mb-8 text-center">Average Customer Savings</h3>
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">$1,200+</div>
                  <div className="text-sm text-white/80">Annual Savings</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">$30,000+</div>
                  <div className="text-sm text-white/80">25-Year Savings</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-energy-yellow mb-2">65%</div>
                  <div className="text-sm text-white/80">Average Bill Reduction</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
              Ready to Join the Solar Revolution?
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Let's discuss how solar energy can transform your property and your energy costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                Schedule Your Free Consultation
              </Link>
              <a
                href="tel:352-509-6933"
                className="bg-solar-blue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-solar-blue/90 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                352-509-6933
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;